<template>
  <smd-one-col-layout class="account-settings"
                      content-styles="account-setting__content-styles">
    <smd-header class="account-setting__header"
                nav
                slot="header">
      <smd-btn class="back-btn"
               slot="left-actions"
               :icon="leftIcon"
               :bg="false"
               @click="leftButtonClicked"/>
      <h2 class="Title3">{{ header }}</h2>
      <div slot="right-actions">
        <smd-btn class="right-btn"
                 v-if="!submitting"
                 :icon="rightIcon"
                 :bg="false"
                 @click="rightButtonClicked"/>
      </div>
    </smd-header>

    <smd-alert-pane v-if="error" type="danger">
      <p>{{ error }}</p>
    </smd-alert-pane>

    <div v-if="submitting" class="loading-spinner">
      <smd-loading-spinner></smd-loading-spinner>
    </div>

    <form @submit.prevent="rightButtonClicked"
          novalidate
          v-else>
      <div class="smd-form__row"
           v-if="showOtherFields">
        <div class="smd-form__col">
          <label for="firstname"
                 input-label>
            First Name
          </label>
          <input id="firstname"
                 ref="firstname"
                 type="text"
                 v-model="$v.firstname.$model"
                 :has-value="!validationErrors.firstname"
                 :readonly="mode !== $options.modes.EDIT">
        </div>
      </div>
      <div class="smd-form__row validation-error"
           v-if="submitAttempted && validationErrors.firstname">
        <small class="error">
          {{ validationErrors.firstname }}
        </small>
      </div>
      <div class="smd-form__row"
           v-if="showOtherFields">
        <div class="smd-form__col">
          <label for="middlename"
                 input-label>
            Middle Name
          </label>
          <input id="middlename"
                 type="text"
                 v-model="middlename"
                 :has-value="!validationErrors.middlename"
                 :readonly="mode === $options.modes.READ">
        </div>
      </div>
      <div class="smd-form__row"
           v-if="showOtherFields">
        <div class="smd-form__col">
          <label for="lastname"
                 input-label>
            Last Name
          </label>
          <input id="lastname"
                 type="text"
                 v-model="lastname"
                 :has-value="!validationErrors.lastname"
                 :readonly="mode === $options.modes.READ">
        </div>
      </div>
      <div class="smd-form__row"
           v-if="showOtherFields">
        <div class="smd-form__col">
          <label for="birthdate"
                 input-label>
            Birthdate
          </label>
          <smd-date-input id="birthdate"
                          v-model="$v.birthdate.$model"
                          :has-value="!validationErrors.birthdate"
                          :readonly="mode === $options.modes.READ"
                          :format-emitted-date="'MM/DD/YYYY'"/>
        </div>
      </div>
      <div class="smd-form__row validation-error"
           v-if="submitAttempted && validationErrors.birthdate">
        <small class="error">
          {{ validationErrors.birthdate }}
        </small>
      </div>
      <div class="smd-form__row"
           v-if="showMobileNumber"
           :class="{ 'with-btn': mode !== $options.modes.EDIT_NUMBER }">
        <div class="smd-form__col">
          <label for="mobilenumber"
                 input-label>
            Mobile Number
          </label>
          <input id="mobilenumber"
                 ref="mobilenumber"
                 type="tel"
                 v-if="mode !== $options.modes.EDIT_NUMBER"
                 v-model="$v.mobilenumber.$model"
                 :has-value="!$v.mobilenumber.$model.$error"
                 :readonly="mode === $options.modes.READ">
          <smd-phone-input id="mobilenumber"
                           ref="mobilenumber"
                           v-if="mode === $options.modes.EDIT_NUMBER"
                           v-model="$v.mobilenumber.$model"
                           :has-value="!$v.mobilenumber.$model.$error"
                           @keyup.enter="rightButtonClicked"/>
          <div class="validation-error"
               v-if="submitAttempted && validationErrors.mobilenumber">
            <small class="error">
              {{ validationErrors.mobilenumber }}
            </small>
          </div>
        </div>
        <div class="smd-form__col"
             v-if="mode !== $options.modes.EDIT_NUMBER">
          <smd-btn section="4"
                   variant="blue-enabled"
                   @click="editMobileNumber">
            <text-string label-id="txt440"/>
          </smd-btn>
        </div>
      </div>
      <div v-if="showEmail"
           class="smd-form__row"
           :class="{ 'with-btn': mode !== $options.modes.EDIT_EMAIL }">
        <div class="smd-form__col">
          <label input-label
                 for="email">
            Email
          </label>
          <input id="email"
                 ref="email"
                 type="email"
                 v-model="email"
                 :has-value="!validationErrors.email"
                 :readonly="mode === $options.modes.READ"
                 @keyup.enter="rightButtonClicked">
          <div v-if="submitAttempted && validationErrors.email">
            <small class="error">
              {{ validationErrors.email }}
            </small>
          </div>
        </div>
        <div class="smd-form__col"
             v-if="mode !== $options.modes.EDIT_EMAIL">
          <smd-btn section="4"
                   variant="blue-enabled"
                   @click="editEmail">
            {{ emailButtonText }}
          </smd-btn>
        </div>
      </div>
    </form>


    <div class="account-settings__footer"
         slot="footer">
      <div class="version">
        <p>{{ $options.appVersion }}</p>
      </div>
      <div class="logout"
           v-if="mode === $options.modes.READ">
        <smd-btn icon="ns-icon-logout"
                 clean
                 @click="logout">
          <div class="Label1">Logout</div>
        </smd-btn>
      </div>
    </div>

    <vue-hcaptcha :sitekey="$options.siteKey"
                  ref="hcaptchaMobile"
                  size="invisible"
                  @error="onMobileHcaptchaError"
                  @verify="onMobileHcaptchaVerify">
    </vue-hcaptcha>

    <vue-hcaptcha :sitekey="$options.siteKey"
                  ref="hcaptchaEmail"
                  size="invisible"
                  @error="onEmailHcaptchaError"
                  @verify="onEmailHcaptchaVerify">
    </vue-hcaptcha>
  </smd-one-col-layout>
</template>

<script>
  import VueHcaptcha from '@hcaptcha/vue-hcaptcha';
  import {mapGetters, mapActions} from 'vuex';
  import {isEmpty, censorMobileNumber} from '@/utils';
  import {AccountService, UPDATE_MOBILE_EMAIL_RESULT} from '@/services/account';
  import {OtpService, UPDATE_MOBILE_EMAIL_OTP_ENUM} from '@/services/otp';
  import {required, email} from 'vuelidate/lib/validators';
  import {bus} from '@/main';
  import {logout} from '@/utils/logout';
  import Vue from 'vue';
  import formValidationMixin from '@/mixins/form-validations';
  import appVersionMixin from '@/mixins/app-version';
  import GetElementString from '@/mixins/get-element-string';

  import {
    GETTER_BIRTHDATE,
    GETTER_EMAIL,
    GETTER_FIRSTNAME,
    GETTER_LASTNAME,
    GETTER_MIDDLENAME,
    GETTER_MOBILENUMBER,
    ACTION_EDIT_ACCOUNT,
  } from '@/store/types';

  import {
    alphaSpaces,
    dateStringIsValid,
    dateStringIsLessThan,
  } from '@/utils/custom-validations';

  import {
    SHOW_OTP_EVENT,
    CLOSE_OTP_EVENT,
    OTP_SUCCESS_EVENT,
  } from '@/constants/bus';

  const todayInMilli = (new Date()).setHours(0, 0, 0, 0).valueOf();

  export default {
    name: 'account-settings',
    mixins: [
      appVersionMixin,
      formValidationMixin,
      GetElementString
    ],
    components: {
      VueHcaptcha,
    },
    data() {
      return {
        mode: this.$options.modes.READ,
        birthdate: '',
        email: '',
        firstname: '',
        lastname: '',
        middlename: '',
        mobilenumber: '',
        submitting: false,
        error: null,
        validateTokenMobile: null,
        validateTokenEmail: null,
      };
    },
    computed: {
      ...mapGetters({
        storeBirthdate: GETTER_BIRTHDATE,
        storeEmail: GETTER_EMAIL,
        storeFirstname: GETTER_FIRSTNAME,
        storeLastname: GETTER_LASTNAME,
        storeMiddlename: GETTER_MIDDLENAME,
        storeMobilenumber: GETTER_MOBILENUMBER,
      }),
      header() {
        return this.mode === this.$options.modes.READ ? 'Account' : 'Editing Account';
      },
      leftIcon() {
        return this.mode === this.$options.modes.READ ? 'ns-icon-ico028' : 'ns-icon-ico027';
      },
      rightIcon() {
        return this.mode === this.$options.modes.READ ? 'ns-icon-edit' : 'ns-icon-check';
      },
      showOtherFields() {
        return this.mode === this.$options.modes.READ || this.mode === this.$options.modes.EDIT;
      },
      showMobileNumber() {
        return this.mode === this.$options.modes.READ || this.mode === this.$options.modes.EDIT_NUMBER;
      },
      showEmail() {
        return this.mode === this.$options.modes.READ || this.mode === this.$options.modes.EDIT_EMAIL;
      },
      emailButtonText() {
        return isEmpty(this.email) ? this.getElementString('txt063') : this.getElementString('txt440');
      },
    },
    watch: {
      storeBirthdate: {
        handler() {
          this.setFormValues();
        },
        immediate: true,
      },
      storeEmail: {
        handler() {
          this.setFormValues();
        },
        immediate: true,
      },
      storeFirstname: {
        handler() {
          this.setFormValues();
        },
        immediate: true,
      },
      storeLastname: {
        handler() {
          this.setFormValues();
        },
        immediate: true,
      },
      storeMiddlename: {
        handler() {
          this.setFormValues();
        },
        immediate: true,
      },
      storeMobilenumber: {
        handler() {
          this.setFormValues();
        },
        immediate: true,
      },
    },
    created() {
      bus.$on(OTP_SUCCESS_EVENT, () => {
        if (this.mode === this.$options.modes.EDIT_NUMBER) {
          this.editAccount({
            mobilenumber: this.mobilenumber,
          });
        } else if (this.mode === this.$options.modes.EDIT_EMAIL) {
          this.editAccount({
            email: this.email,
          });
        }

        bus.$emit(CLOSE_OTP_EVENT);
        this.mode = this.$options.modes.READ;
      });
    },
    destroyed() {
      bus.$off(OTP_SUCCESS_EVENT);
    },
    methods: {
      ...mapActions({
        editAccount: ACTION_EDIT_ACCOUNT,
      }),
      setFormValues() {
        this.birthdate = this.storeBirthdate;
        this.email = this.storeEmail;
        this.firstname = this.storeFirstname;
        this.lastname = this.storeLastname;
        this.middlename = this.storeMiddlename;
        this.mobilenumber = this.storeMobilenumber;
      },
      editMobileNumber() {
        this.mode = this.$options.modes.EDIT_NUMBER;
        Vue.nextTick(() => {
          this.$refs.mobilenumber.focus();
        });
      },
      editEmail() {
        this.mode = this.$options.modes.EDIT_EMAIL;
        Vue.nextTick(() => {
          this.$refs.email.focus();
        });
      },
      cancelEditMode() {
        this.$v.$reset();
        this.setFormValues();
        this.submitAttempted = false;
        this.mode = this.$options.modes.READ;
      },
      leftButtonClicked() {
        if (this.mode === this.$options.modes.READ) {
          this.$router.go(-1);
        } else {
          this.cancelEditMode();
        }
      },
      rightButtonClicked() {
        if (this.submitting) return;

        if (this.mode === this.$options.modes.READ) {
          this.mode = this.$options.modes.EDIT;
          this.$refs.firstname.focus();
          return;
        }

        this.$v.$touch();
        this.submitAttempted = true;

        if (!this.$v.$anyDirty) {
          this.cancelEditMode();
          return;
        }

        if (this.mode === this.$options.modes.EDIT) {
          if (this.$v.$invalid) {
            this.focusOnFieldWithError();
          } else {
            this.updateDetails();
          }
        } else if (this.mode === this.$options.modes.EDIT_NUMBER) {
          if (this.mobilenumber === this.storeMobilenumber) {
            this.cancelEditMode();
          } else if (this.$v.mobilenumber.$invalid) {
            this.focusOnFieldWithError();
          } else {
            this.updateMobileNumber();
          }
        } else if (this.mode === this.$options.modes.EDIT_EMAIL) {
          if (this.email === this.storeEmail) {
            this.cancelEditMode();
          } else if (this.$v.email.$invalid) {
            this.focusOnFieldWithError();
          } else {
            this.updateEmail();
          }
        }
      },
      async updateDetails() {
        this.error = null;
        this.submitting = true;

        try {
          const details = {
            firstname: this.firstname,
            middlename: this.middlename,
            lastname: this.lastname,
            birthdate: this.birthdate,
          };
          const {data: {result}} = await AccountService.updateAccount(details);

          this.submitting = false;

          if (!result) {
            this.error = 'Updating account failed.';
          } else {
            this.editAccount(details);
            this.mode = this.$options.modes.READ;
          }
        } catch (error) {
          this.submitting = false;
          this.error = 'Updating account failed.';
        }
      },
      onMobileHcaptchaError(e) {
        console.error(e);
        this.error = 'Failed to load mobile captcha.';
      },
      onMobileHcaptchaVerify(token) {
        this.validateTokenMobile = token;
        this.updateMobileNumber();
      },
      async updateMobileNumber() {
        if (!this.validateTokenMobile) {
          this.$refs.hcaptchaMobile.execute();
          return;
        }

        try {
          const {data: {result, verifytoken}} = await AccountService.updateAccountMobileNumber(this.mobilenumber, this.validateTokenMobile);

          this.submitting = false;

          if (result === UPDATE_MOBILE_EMAIL_RESULT.SUCCESS) {
            bus.$emit(SHOW_OTP_EVENT, {
              sentTo: censorMobileNumber(this.mobilenumber),
              initialVerifyToken: verifytoken,
              verifyApiService: OtpService.mobileUpdateVerify,
              successVerifyCodes: [UPDATE_MOBILE_EMAIL_OTP_ENUM.SUCCESS],
              failedVerifyCodes: {
                [UPDATE_MOBILE_EMAIL_OTP_ENUM.INCORRECT_TOKEN]: 'Verification failed.',
                [UPDATE_MOBILE_EMAIL_OTP_ENUM.ALREADY_TAKEN]: 'Mobile number already taken.',
                [UPDATE_MOBILE_EMAIL_OTP_ENUM.EXPIRED]: 'Verification code is already expired.',
                [UPDATE_MOBILE_EMAIL_OTP_ENUM.FAILED]: 'Invalid verification code.',
              },
            });
          } else if (result === UPDATE_MOBILE_EMAIL_RESULT.ALREADY_EXISTS) {
            this.error = 'Mobile number already exists.';
          } else {
            this.error = 'Updating mobile number failed';
          }
        } catch (error) {
          this.submitting = false;
          this.error = 'Updating mobile number failed';
        }
      },
      onEmailHcaptchaError(e) {
        console.error(e);
        this.error = 'Failed to load mobile captcha.';
      },
      onEmailHcaptchaVerify(token) {
        this.validateTokenEmail = token;
        this.updateEmail();
      },
      async updateEmail() {
        if (!this.validateTokenEmail) {
          this.$refs.hcaptchaEmail.execute();
          return;
        }

        try {
          const {data: {result, verifytoken}} = await AccountService.updateAccountEmail(this.email, this.validateTokenEmail);

          this.submitting = false;

          if (result === UPDATE_MOBILE_EMAIL_RESULT.SUCCESS) {
            bus.$emit(SHOW_OTP_EVENT, {
              sentTo: this.email,
              initialVerifyToken: verifytoken,
              verifyApiService: OtpService.emailUpdateVerify,
              successVerifyCodes: [UPDATE_MOBILE_EMAIL_OTP_ENUM.SUCCESS],
              failedVerifyCodes: {
                [UPDATE_MOBILE_EMAIL_OTP_ENUM.INCORRECT_TOKEN]: 'Verification failed.',
                [UPDATE_MOBILE_EMAIL_OTP_ENUM.ALREADY_TAKEN]: 'Email is already taken.',
                [UPDATE_MOBILE_EMAIL_OTP_ENUM.EXPIRED]: 'Verification code is already expired.',
                [UPDATE_MOBILE_EMAIL_OTP_ENUM.FAILED]: 'Invalid verification code.',
              },
            });
          } else if (result === UPDATE_MOBILE_EMAIL_RESULT.ALREADY_EXISTS) {
            this.error = 'Email already exists.';
          } else {
            this.error = 'Updating email failed.';
          }
        } catch (error) {
          this.submitting = false;
          this.error = 'Updating email failed.';
        }
      },
      logout,
    },
    modes: {
      READ: 'READ',
      EDIT: 'EDIT',
      EDIT_NUMBER: 'EDIT_NUMBER',
      EDIT_EMAIL: 'EDIT_EMAIL',
    },
    validations: {
      mobilenumber: {
        required,
      },
      firstname: {
        required,
        alphaSpaces,
      },
      middlename: {
        alphaSpaces,
      },
      lastname: {
        required,
        alphaSpaces,
      },
      email: {
        email,
      },
      birthdate: {
        required,
        dateStringIsValid,
        dateStringIsLessThan: dateStringIsLessThan(todayInMilli),
      },
    },
    mapFieldNames: {
      firstname: 'First name',
      middlename: 'Middle name',
      lastname: 'Last name',
      birthdate: 'Date of birth',
      mobilenumber: 'Mobile number',
      email: 'Email',
    },
    siteKey: '6979ef7d-361a-4083-bb28-6d40bf4bf641',
  }
</script>

<style lang="scss">

  .account-settings {
    .loading-spinner {
      margin-left: auto;
    }

    .smd-alert-pane {
      margin-bottom: space(m);
    }

    .with-btn {
      align-items: flex-end;

      button {
        min-width: unset !important;
      }

      .smd-form__col {

        &:last-child {
          flex: unset;
        }
      }
    }
  }

  .account-setting__header {
    h2 {
      color: color(gray-400);
    }

    .right-btn {
      color: color(gray-400);

      &:hover {
        background: none;
      }

      span {
        font-size: 14px;
      }

      .ns-icon-check {
        color: color(dodgerBlue);
        font-size: 12px;
      }
    }
  }

  .account-settings__footer {
    button {
      @extend %layout--flex-row-center-x;
      color: color(label, text);

      span {
        font-size: font(base);
        margin-right: space(xs)+2;
      }
    }

    .logout {
      border-top: 1px solid color(dark, border);
      padding-top: space(m);
      margin-bottom: space(s);

      @include responsive(tablet) {
        display: block;
      }

      [class^="ns-icon-"] {
        @include responsive(desktop) {
          font-size: 24px;
        }
        margin-right: space(s);
      }
    }

    .version {
      text-align: center;
      font-size: font(s);
      color: color(label, text);
      margin-bottom: space(xs);
    }
  }

  .account-setting__content-styles {
    > div {
      @include set-max-width($max-width: 800px);
    }
  }
</style>
